// One visible online cover at a time, with a pause between attempts. The queue
// only calls our authenticated API; platform URLs never enter the browser.
const MAX_WAITING = 64
const MIN_INTERVAL_MS = 1200
const MAX_BYTES = 2 * 1024 * 1024

type Job = {
  url: string
  session: string
  signal: AbortSignal
  resolve: (blob: Blob) => void
  reject: (reason: unknown) => void
}
const waiting: Job[] = []
let running = false
let lastStart = 0

function aborted(): DOMException {
  return new DOMException('Cover request cancelled', 'AbortError')
}

function pause(ms: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.reject(aborted())
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => { signal.removeEventListener('abort', stop); resolve() }, ms)
    function stop(): void { window.clearTimeout(timer); signal.removeEventListener('abort', stop); reject(aborted()) }
    signal.addEventListener('abort', stop, { once: true })
  })
}

async function transfer(job: Job): Promise<Blob> {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    await pause(Math.max(0, lastStart + MIN_INTERVAL_MS - Date.now()), job.signal)
    if (job.signal.aborted) throw aborted()
    lastStart = Date.now()
    const response = await fetch(job.url, {
      method: 'POST', credentials: 'same-origin', cache: 'no-store', referrerPolicy: 'no-referrer',
      headers: { 'Content-Type': 'application/json', 'X-DTK-Cover-Session': job.session },
      body: '{}', signal: job.signal,
    })
    // A local 429 means the API/sidecar is busy, not that the platform was
    // contacted. Honour Retry-After and cap attempts; all upstream failures stop.
    if (response.status === 429 && attempt < 2) {
      const raw = Number(response.headers.get('Retry-After') ?? 1)
      const seconds = Number.isFinite(raw) ? Math.max(1, Math.min(raw, 60)) : 1
      await pause(seconds * 1000, job.signal)
      continue
    }
    if (!response.ok) throw new Error('Online cover unavailable')
    const type = response.headers.get('Content-Type')?.split(';', 1)[0]?.trim().toLowerCase()
    const length = Number(response.headers.get('Content-Length'))
    if ((type !== 'image/jpeg' && type !== 'image/png') || length > MAX_BYTES) throw new Error('Invalid online cover')
    const blob = await response.blob()
    if (blob.size === 0 || blob.size > MAX_BYTES) throw new Error('Invalid online cover')
    return blob
  }
  throw new Error('Online cover unavailable')
}

async function drain(): Promise<void> {
  if (running) return
  running = true
  try {
    while (waiting.length > 0) {
      const job = waiting.shift()
      if (!job) continue
      if (job.signal.aborted) { job.reject(aborted()); continue }
      try { job.resolve(await transfer(job)) } catch (error) { job.reject(error) }
    }
  } finally { running = false }
}

/** A user-approved request; aborting removes its opportunity to contact the API. */
export function loadRemoteCover(url: string, session: string, signal: AbortSignal): Promise<Blob> {
  if (!url.startsWith('/api/v1/archive/') || !url.endsWith('/cover') || !/^[0-9a-f]{16}$/.test(session)) {
    return Promise.reject(new Error('Invalid online cover path'))
  }
  if (signal.aborted) return Promise.reject(aborted())
  if (waiting.length >= MAX_WAITING) return Promise.reject(new Error('Online cover queue full'))
  return new Promise((resolve, reject) => {
    const job: Job = { url, session, signal, resolve, reject }
    const stop = () => {
      const index = waiting.indexOf(job)
      if (index >= 0) { waiting.splice(index, 1); reject(aborted()) }
    }
    signal.addEventListener('abort', stop, { once: true })
    const cleanupResolve = job.resolve
    const cleanupReject = job.reject
    job.resolve = (blob) => { signal.removeEventListener('abort', stop); cleanupResolve(blob) }
    job.reject = (error) => { signal.removeEventListener('abort', stop); cleanupReject(error) }
    waiting.push(job)
    // Deferring avoids a speculative request from React strict-mode mount/unmount.
    queueMicrotask(() => { void drain() })
  })
}
